/* hs-eslint ignored failing-rules */

/* eslint-disable promise/catch-or-return */
'use es6';

import * as api from 'laboratory-lib/core/api';
import { resolveTreatments, createAsyncResolver } from 'laboratory-lib/core/utils';
import { getOverridesFromSearch, getOverridesFromCache, getCacheKeys, saveOverridesToCache } from 'laboratory-lib/core/overrides';
import { validateCallback } from 'laboratory-lib/core/validators';
import { filterObject, once } from 'laboratory-lib/core/helpers';

function getAsyncNetworkAdapter({
  apiDomain,
  ceid,
  http,
  quickFetch,
  quickFetchLabel,
  schema,
  identifiers,
  timeout
} = {}) {
  const getDataFromHttp = (opts = {}) => {
    api.getTreatmentsFromHttp({
      apiDomain,
      // this removes all the properties that are empty or undefined
      query: filterObject(Object.assign({}, identifiers, {
        ceid
      })),
      data: Object.keys(schema).map(key => ({
        key,
        identifier: schema[key].identifier
      })),
      http,
      onComplete: opts.onComplete,
      onError: opts.onError,
      timeout
    });
  };

  const httpRetryHandler = (opts = {}) => err => {
    // don't retry for timeouts
    const message = err ? err.message || err || '' : '';

    if (message.indexOf('timeout') === -1) {
      getDataFromHttp(opts);
    } else {
      opts.onError(err);
    }
  };

  const getDataFromQuickFetch = (opts = {}) => {
    api.getTreatmentsFromQuickFetch({
      onComplete: opts.onComplete,
      onError: httpRetryHandler(opts),
      quickFetch,
      quickFetchLabel,
      timeout
    });
  };

  if (quickFetchLabel) {
    return getDataFromQuickFetch;
  }

  return getDataFromHttp;
}

function wrapNetworkAdapterWithMeta({
  asyncStore,
  cacheKeys,
  ceid,
  search,
  schema,
  networkAdapter,
  onError
}) {
  const overridesFromSearch = getOverridesFromSearch({
    cacheKeys,
    search
  });
  const asyncOverridesFromCache = getOverridesFromCache({
    cacheKeys,
    asyncStore,
    onError
  });
  const data = {
    overridesFromCache: {},
    overridesFromSearch,
    schema,
    treatments: []
  };
  return (opts = {}) => {
    asyncOverridesFromCache.then(overridesFromCache => {
      data.overridesFromCache = overridesFromCache;
      const requiresNetwork = !!ceid || !cacheKeys.length || cacheKeys.length && cacheKeys.some(({
        key
      }) => !overridesFromCache[key]);

      if (requiresNetwork) {
        networkAdapter({
          onComplete: (treatments = []) => {
            data.treatments = treatments;
            opts.onComplete(data);
          },
          onError: err => {
            opts.onError(err, data);
          }
        });
      } else {
        opts.onComplete(data);
      }
    });
  };
}

export function createTreatmentsResolver({
  asyncStore,
  ceid,
  http,
  onError,
  quickFetch,
  quickFetchLabel,
  search,
  schema,
  identifiers,
  timeout
} = {}) {
  let resolved = false;
  const cacheKeys = getCacheKeys({
    schema
  });
  const asyncNetworkAdapter = getAsyncNetworkAdapter({
    ceid,
    http,
    quickFetch,
    quickFetchLabel,
    schema,
    identifiers,
    timeout
  });
  const getAsyncData = wrapNetworkAdapterWithMeta({
    asyncStore,
    cacheKeys,
    ceid,
    search,
    schema,
    networkAdapter: asyncNetworkAdapter,
    onError
  });
  const asyncTreatmentsResolver = createAsyncResolver({
    getAsyncData,
    resolve: once(resolveTreatments),
    onError
  });
  return {
    resolveTreatments: callback => {
      validateCallback(callback, 'treatmentsClient.resolve', 'treatmentsClient.resolve((treatments) => {}');
      asyncTreatmentsResolver(treatments => {
        if (!resolved) {
          saveOverridesToCache({
            schema,
            treatments,
            asyncStore,
            onError
          });
        }

        callback(treatments);
        resolved = true;
      });
    }
  };
}